import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chips,
  Icon,
  IconButton,
  InputAdornment,
  Pagination,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@gupshup-frontend/gupshup-ts-components';
import TemplatePreview from '../../components/TemplatePreview';
import { mapTemplate } from '../../helper/utils';
import { toFormBodyNumbering } from '../../helper/templateVariables';
import {
  STATUS_FILTERS,
  statusMeta,
  isEditable,
  qualityMeta,
  APPROVED_STATUS_VALUE,
  QUALITY_VISIBLE_STATUSES,
} from '../../helper/templateStatus';
import { ERROR_COPY } from '../../utils/constants';

// Console's own default page size for this list.
const PAGE_SIZE = 5;

// Console's own clone icon (gs-ui-templates' TEMPLATE_ICONS.CLONE, constants/icons.ts) — passed
// through as a Button menu item's customIconPath (renders as a plain <img>, verified against the
// compiled component) so the split-button menu shows the identical glyph the console uses instead
// of an approximate library stand-in.
const CLONE_ICON_URL = 'https://gs-upload.gupshup.io/templates/icons/ic_clone.svg';

// Console's own sync icon (gs-ui-templates' SyncTemplates.tsx -> TEMPLATE_ICONS.SYNC,
// constants/icons.ts) — same glyph, different action: the console's own button also asks CTM to
// resync templates from Meta, and our Enterprise path has no such call, so ours only re-reads the
// list from gs-intg. Tooltip below says "Refresh templates", not the console's "Sync templates
// with your Facebook business manager".
const REFRESH_ICON = 'https://gs-upload.gupshup.io/templates/icons/ic_sync.svg';

// Console's own stated minimum — below this the search box is inert and every row shows.
const MIN_SEARCH_CHARS = 3;

// Left rail order: "All" first (own hardcoded label), then STATUS_FILTERS in its own declared
// order with ALL pulled out of the tail — same six values the old chip row used, just laid out
// as the console's plain vertical list instead of chips.
const RAIL_STATUS_VALUES = ['ALL', ...STATUS_FILTERS.filter((value) => value !== 'ALL')];

// The card footer wants a date-only "3 Sept 2026" shape — day, abbreviated month, year, no
// time — matching the console's own copy in that spot exactly, down to its 4-letter "Sept"
// for that one month (every other month keeps the usual 3-letter abbreviation).
const CARD_DATE_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const formatCardDate = (epochMs) => {
  if (!epochMs) return '—';
  const date = new Date(epochMs);
  return `${date.getDate()} ${CARD_DATE_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

// Sort key for "newest modified first" below. A template missing updation_time (or carrying
// something Number() can't parse) resolves to -Infinity — the oldest possible value — so it
// sorts to the bottom instead of throwing, landing at position 0, or interleaving randomly
// among templates that do have a real timestamp.
const getUpdationTime = (template) => {
  const raw = template?.updation_time;
  if (raw === null || raw === undefined || raw === '') return -Infinity;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : -Infinity;
};

// Filter-aware empty-state copy. ENABLED keeps ERROR_COPY.emptyTemplates as its one true use;
// every other filter derives its own line so it doesn't misreport (e.g. claiming "no approved
// templates" while the Rejected filter is the one active).
const emptyStateCopy = (filter) => {
  if (filter === 'ENABLED') return ERROR_COPY.emptyTemplates;
  if (filter === 'ALL') return 'No templates on this channel';
  const { label } = statusMeta(filter);
  // Lower-case only the leading letter — a full toLowerCase() would turn "Disabled by WhatsApp"
  // into "...whatsapp", losing the brand's own capitalization mid-sentence.
  const midSentence = label.charAt(0).toLowerCase() + label.slice(1);
  return `No ${midSentence} templates on this channel`;
};

// Presentational — everything arrives as props, nothing is fetched here. Search is the one
// exception: the list endpoint has no search param, so it runs client-side over the
// already-fetched `templates` array and its query text lives in local state rather than a
// new prop (List's prop contract is unchanged from the table version of this component).
const List = ({
  channels,
  selectedChannelId,
  onChannelChange,
  statusFilter,
  onStatusChange,
  templates,
  loading,
  page,
  onPageChange,
  onRefresh,
  onCreate,
  onView,
  onEdit,
  onClone,
  onDelete,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();

  // Pale background + dark text per tone, matching the console's own bespoke Chip
  // (gs-ui-templates Chip/style.module.css bg_red/bg_green/bg_yellow/bg_gray) instead of MUI
  // Chip's default solid `color="error"`/`color="warning"` fill. Same theme paths this
  // library's own Alert component already resolves its per-severity backgrounds from
  // (confirmed against the compiled Alert-*.js chunk) — not a second, independently-guessed
  // palette. Keys are this file's own tone vocabulary (see templateStatus.js TEMPLATE_STATUS /
  // QUALITY_RATINGS), with `neutral` doubling as the fallback for any unmapped tone.
  const toneStyles = useMemo(
    () => ({
      success: { backgroundColor: theme.palette.success.light, color: theme.palette.common.black },
      error: { backgroundColor: theme.custom.colors.error.lighter, color: theme.palette.common.black },
      // Alert hardcodes this literal rather than theme.custom.colors.warning.lighter (#FFB678 —
      // a saturated orange, not pale), so this stays a literal too instead of swapping in a
      // "themed" value that would actually look less correct.
      warning: { backgroundColor: '#FDEBD0', color: theme.palette.common.black },
      neutral: { backgroundColor: theme.palette.grey[200], color: theme.palette.common.black },
    }),
    [theme],
  );

  // A changed query can easily leave `page` pointing past the end of the now-smaller filtered
  // result — reset to page 0 every time the query itself changes, the same way the parent
  // already does for a changed channel/status (Templates/index.js).
  useEffect(() => {
    onPageChange(0);
  }, [searchQuery, onPageChange]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const searchActive = normalizedQuery.length >= MIN_SEARCH_CHARS;

  // Newest-modified-first, sorted client-side. The upstream endpoint (ce-api's
  // GetWhatsappHSM.java:34-53) supports id/name/type/status/wabaId filters, a timestamp range,
  // and real limit/offset pagination (ChannelHSMTableDao.java:290 runs `limit ? offset ?`) — but
  // no orderBy param, and neither HSM DAO's select carries an ORDER BY, so row order is
  // unspecified and can't be pushed down. The console's own orderBy=modifiedOn&order=desc works
  // only because it targets CTM (ctm-api.gupshup.io), a separate service with its own database,
  // not on our path — so we also skip offset pagination: paging an unordered set would be worse
  // than fetching one limit-bounded page and sorting it, meaning "newest" here is newest among
  // what limit returned, not globally. This sort runs before the search filter and pagination
  // slice below so page 1 always leads with the most recently modified template.
  // `[...templates]` copies first — Array.prototype.sort mutates in place and `templates` is a
  // prop this component doesn't own.
  const sortedTemplates = useMemo(
    () => [...templates].sort((a, b) => {
      const aTime = getUpdationTime(a);
      const bTime = getUpdationTime(b);
      if (aTime === bTime) return 0;
      return aTime > bTime ? -1 : 1;
    }),
    [templates],
  );

  // Status is already server-side filtered by the time `templates` arrives — this only ever
  // narrows further (name / id / body, case-insensitive substring), never re-applies a status
  // filter of its own.
  // Memoized — a parent re-render that leaves sortedTemplates/searchActive/normalizedQuery
  // unchanged (e.g. a sibling toast opening/closing) would otherwise re-scan and re-lowercase
  // every template on every render for nothing.
  const filteredTemplates = useMemo(() => (
    searchActive
      ? sortedTemplates.filter((template) => {
          const name = String(template.name || '').toLowerCase();
          const id = String(template.id ?? '').toLowerCase();
          const body = String(template.body || '').toLowerCase();
          return name.includes(normalizedQuery) || id.includes(normalizedQuery) || body.includes(normalizedQuery);
        })
      : sortedTemplates
  ), [sortedTemplates, searchActive, normalizedQuery]);

  // The enterprise list endpoint has no limit/offset — it always returns the full array for the
  // channel/status pair, so pagination here is a client-side slice over that already-filtered
  // list. Clamped defensively so one stale render between a search narrowing the result set and
  // the reset effect above actually landing never shows an accidental "no results" page.
  const maxPage = Math.max(0, Math.ceil(filteredTemplates.length / PAGE_SIZE) - 1);
  const currentPage = Math.min(page, maxPage);
  const startIndex = currentPage * PAGE_SIZE;
  const pageTemplates = useMemo(
    () => filteredTemplates.slice(startIndex, startIndex + PAGE_SIZE),
    [filteredTemplates, startIndex],
  );

  return (
    <div className="br-tpl">
      <div className="br-tpl-header">
        <div className="br-tpl-header-copy">
          <Typography variant="h5" className="br-tpl-header-title" data-testid="bloomreach-templates-heading">
            Templates
          </Typography>
          <Typography variant="body2" className="br-tpl-header-subtitle">
            Create, preview and manage the WhatsApp templates available on this channel.
          </Typography>
        </div>
        <div className="br-tpl-header-actions">
          {/* MUI Tooltip needs a non-disabled child to still get hover/focus events while the
              button itself is disabled (loading) — the wrapping span is that child. */}
          <Tooltip placement="top" title="Refresh templates" data-testid="bloomreach-templates-refresh-tooltip">
            <span>
              <Button
                size="small"
                variant="outlined"
                customStartIcon={REFRESH_ICON}
                onClick={onRefresh}
                disabled={loading}
                aria-label="Refresh templates"
                className="br-tpl-refresh-button"
                data-testid="bloomreach-templates-refresh-button"
              />
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            startIcon="plus"
            onClick={onCreate}
            data-testid="bloomreach-templates-create-button"
          >
            Create Template
          </Button>
        </div>
      </div>

      <div className="br-tpl-channel-row">
        <Select
          size="small"
          label="Channel"
          items={channels}
          value={selectedChannelId || ''}
          handleChange={onChannelChange}
          data-testid="bloomreach-templates-channel-select"
        />
      </div>

      <div className="br-tpl-layout">
        <nav
          className="br-tpl-rail"
          aria-labelledby="bloomreach-templates-status-rail-heading"
          data-testid="bloomreach-templates-status-rail"
        >
          <p
            id="bloomreach-templates-status-rail-heading"
            className="br-tpl-rail-heading"
            data-testid="bloomreach-templates-status-rail-heading"
          >
            Status
          </p>
          {RAIL_STATUS_VALUES.map((value) => {
            const active = value === statusFilter;
            return (
              <button
                type="button"
                key={value}
                className={`br-tpl-rail-item${active ? ' br-tpl-rail-item--active' : ''}`}
                onClick={() => onStatusChange(value)}
                data-testid={`bloomreach-templates-status-filter-${value.toLowerCase()}`}
              >
                {value === 'ALL' ? 'All' : statusMeta(value).label}
              </button>
            );
          })}
        </nav>
        {/*
          Filtering is server-side via the status query param — never re-filter templates by
          status client-side here. Verified on dev 2026-09-24: status=ALL returned Pending and
          Rejected rows too, not just ENABLED, so dev does apply this param — the earlier "dev
          ignores it" note here was stale.
        */}

        <div className="br-tpl-main">
          <div className="br-tpl-search-row">
            <TextField
              size="small"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by name, id & content"
              helperText={`*Enter at least ${MIN_SEARCH_CHARS} characters to initiate search`}
              className="br-tpl-search-input"
              data-testid="bloomreach-templates-search-input"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon name="magnifyingGlass" size={18} />
                  </InputAdornment>
                ),
              }}
            />
            {/*
              The real console's own toolbar also carries grid/list view toggle icons, a
              Sort-by dropdown, a filter icon, a Compare button and a refresh icon here. None
              of them have an equivalent in our data or scope right now (one status dimension,
              one sort order, no cross-template comparison) — deliberately deferred, not
              forgotten.
            */}
          </div>

          {loading ? (
            <div className="br-tpl-card-grid" data-testid="bloomreach-templates-loading">
              {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <div className="br-tpl-card br-tpl-card-skeleton" key={index}>
                  <div className="br-tpl-skeleton-bar" style={{ width: '70%', height: 16 }} />
                  <div className="br-tpl-skeleton-bar" style={{ width: '40%', height: 20 }} />
                  <div className="br-tpl-skeleton-block" />
                  <div className="br-tpl-skeleton-bar" style={{ width: '50%', height: 14 }} />
                </div>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <p className="br-tpl-empty" data-testid="bloomreach-templates-empty">
              {emptyStateCopy(statusFilter)}
            </p>
          ) : filteredTemplates.length === 0 ? (
            <p className="br-tpl-empty" data-testid="bloomreach-templates-empty-search">
              No templates match your search.
            </p>
          ) : (
            <>
              <div className="br-tpl-card-grid" data-testid="bloomreach-templates-grid">
                {pageTemplates.map((template) => {
                  const id = String(template.id);
                  const editable = isEditable(template.status);
                  const statusInfo = statusMeta(template.status);
                  // gs-ui-templates CardHeader.tsx:119-137: status chip hides only on Approved;
                  // quality chip only ever shows for Approved or Paused (Paused is the one status
                  // where both chips render together).
                  const showStatusChip = template.status !== APPROVED_STATUS_VALUE;
                  const showQualityChip = QUALITY_VISIBLE_STATUSES.includes(template.status);
                  const qualityInfo = qualityMeta(template.quality_score);
                  // mapTemplate (helper/utils.js) adapts an Enterprise list row into the
                  // synthetic containerMeta shape TemplatePreview expects — same call, same
                  // hardcoded index 0, as DetailModal.js already makes; the index only ever
                  // feeds an unused `value` field on the wrapper object. Body is normalized to
                  // this app's {{1}}-based numbering first (see helper/templateVariables.js) so
                  // the card preview reads the same as the console's, not Enterprise's own
                  // header-relative numbering — a shallow copy, mapTemplate itself is untouched
                  // and shared with the Message tab, which deliberately keeps Enterprise's numbering.
                  const previewTemplate = mapTemplate(
                    { ...template, body: toFormBodyNumbering(template.header, template.body) },
                    0,
                  ).details;

                  return (
                    <div className="br-tpl-card" key={id} data-testid={`bloomreach-templates-card-${id}`}>
                      <div className="br-tpl-card-header">
                        <span
                          className="br-tpl-card-title"
                          title={template.name}
                          data-testid={`bloomreach-templates-card-${id}-name`}
                        >
                          {template.name || '—'}
                        </span>
                        <Chips
                          config={{ direction: 'row', flexWrap: 'wrap', gap: 0.5 }}
                          data={[
                            ...(showQualityChip
                              ? [
                                  {
                                    label: qualityInfo.label,
                                    size: 'small',
                                    sx: toneStyles[qualityInfo.tone] || toneStyles.neutral,
                                    'data-testid': `bloomreach-templates-card-${id}-quality-chip`,
                                  },
                                ]
                              : []),
                            ...(showStatusChip
                              ? [
                                  {
                                    label: statusInfo.label,
                                    size: 'small',
                                    sx: toneStyles[statusInfo.tone] || toneStyles.neutral,
                                    'data-testid': `bloomreach-templates-card-${id}-status-chip`,
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </div>

                      <div className="br-tpl-card-preview" data-testid={`bloomreach-templates-card-${id}-preview`}>
                        <TemplatePreview template={previewTemplate} width="100%" height="100%" showHeading={false} />
                      </div>

                      <div className="br-tpl-card-footer">
                        <p className="br-tpl-card-updated" data-testid={`bloomreach-templates-card-${id}-updated`}>
                          Last Updated: {formatCardDate(template.updation_time)}
                        </p>
                      </div>

                      <div className="br-tpl-card-actions">
                        <Button
                          splitButton
                          size="small"
                          variant="outlined"
                          disableSplitButtonPortal={false}
                          data-testid={`bloomreach-templates-card-${id}-manage`}
                          menuItemsData={[
                            {
                              // Console labels this menu entry "Edit Template", not bare "Edit"
                              // (gs-ui-templates constants/common.ts TEMPLATE_MENU_ITEMS ->
                              // i18n/locales/en.json edit_template) — matched here so all three
                              // entries read consistently.
                              text: 'Edit Template',
                              iconName: 'edit',
                              disabled: !editable,
                              onClick: () => editable && onEdit(template),
                              dataTestId: `bloomreach-templates-card-${id}-manage-edit`,
                            },
                            {
                              // Console's own Clone Template action (clone_template in the same
                              // menu list) — prefills the create form from this row with the name
                              // cleared, never a server-side copy. Shown for every status, same
                              // as the console.
                              text: 'Clone Template',
                              customIconPath: CLONE_ICON_URL,
                              onClick: () => onClone(template),
                              dataTestId: `bloomreach-templates-card-${id}-manage-clone`,
                            },
                            {
                              text: 'Delete',
                              iconName: 'trashSimple',
                              onClick: () => onDelete(template),
                              dataTestId: `bloomreach-templates-card-${id}-manage-delete`,
                            },
                          ]}
                        >
                          Manage
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => onView(template)}
                          data-testid={`bloomreach-templates-card-${id}-view`}
                        >
                          <Icon name="info" size={18} />
                        </IconButton>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination's own `page` prop is 1-indexed; the `page` this component receives is
                  0-indexed (it drives the slice math above), so the boundary conversion happens here. */}
              <div className="br-tpl-pagination-row">
                <Pagination
                  data-testid="bloomreach-templates-pagination"
                  total={filteredTemplates.length}
                  page={currentPage + 1}
                  showPerPage={PAGE_SIZE}
                  hideShowPerPage
                  handleShowPerPageChange={() => {}}
                  handlePageChange={(nextPageOneIndexed) => onPageChange(nextPageOneIndexed - 1)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default List;
